import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Bot, Mail } from "lucide-react";
import ChatHint from "../components/ChatHint";
import EmptyStateCards from "../components/EmptyStateCards";

const STATUS = [
  { v: "neu", label: "Neu" },
  { v: "kontaktiert", label: "Kontaktiert" },
  { v: "qualifiziert", label: "Qualifiziert" },
  { v: "verloren", label: "Verloren" },
];
const STATUS_COLOR = {
  neu: "bg-slate-500/15 text-slate-300 border-slate-500/20",
  kontaktiert: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  qualifiziert: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  verloren: "bg-red-500/15 text-red-400 border-red-500/20",
};

const emptyForm = {
  name: "", email: "", phone: "", company: "", source: "",
  status: "neu", notes: "", tags: "",
};

export default function Leads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...l, email: l.email || "", tags: (l.tags || []).join(", ") });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      email: form.email || null,
      tags: typeof form.tags === "string"
        ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : form.tags,
    };
    if (!payload.email) delete payload.email;
    try {
      if (editing) {
        await api.put(`/leads/${editing.id}`, payload);
        toast.success("Lead aktualisiert");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead erstellt");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Speichern");
    }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/leads/${confirmDel.id}`);
      toast.success("Lead gelöscht");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="leads-page">
      <header className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Kontakte</div>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Leads</h1>
          <p className="text-sm text-slate-400 mt-2">{items.length === 0 ? "Noch leer." : `${items.length} Kontakte.`}</p>
          <div className="mt-2"><ChatHint text={'Sag dem KI-Agenten: „Lege einen Lead für Maria Müller an.“'} /></div>
        </div>
        <Button onClick={openCreate} data-testid="add-lead-button" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20">
          <Plus className="h-4 w-4 mr-2" /> Neuer Lead
        </Button>
      </header>

      <Card className="bg-[#111827] border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Lade…</div>
          ) : items.length === 0 ? (
            <EmptyStateCards
              testid="leads-empty"
              title="Noch keine Leads"
              subtitle="Starte mit einem einzigen Kontakt — Name genügt."
              cards={[
                { icon: Plus, title: "Lead anlegen", body: "Nur Name — alles weitere später ergänzen.", onClick: openCreate, testid: "empty-card-create" },
                { icon: Bot, title: "KI-Agent fragen", body: '„Lege Maria Müller als neuen Lead an“ — fertig.', to: "/ki-agent", testid: "empty-card-chat" },
                { icon: Mail, title: "Von Landingpage", body: "Veröffentliche eine Seite — neue Leads fließen automatisch ein.", to: "/seiten", testid: "empty-card-lp" },
              ]}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Firma</TableHead>
                  <TableHead className="text-slate-400">Kontakt</TableHead>
                  <TableHead className="text-slate-400">Quelle</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`lead-row-${l.id}`}>
                    <TableCell>
                      <div className="font-medium text-white">{l.name}</div>
                      <div className="text-xs text-slate-500 truncate max-w-xs">{l.notes}</div>
                    </TableCell>
                    <TableCell className="text-slate-300">{l.company || "—"}</TableCell>
                    <TableCell className="text-slate-400 text-xs">
                      <div>{l.email || "—"}</div>
                      <div>{l.phone || ""}</div>
                    </TableCell>
                    <TableCell className="text-slate-300">{l.source || "—"}</TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLOR[l.status]} border font-normal`} data-testid={`lead-status-${l.id}`}>
                        {STATUS.find((s) => s.v === l.status)?.label || l.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(l)}
                        data-testid={`lead-edit-${l.id}`} className="text-slate-300 hover:text-white hover:bg-white/5">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDel(l)}
                        data-testid={`lead-delete-${l.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl" data-testid="lead-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Lead bearbeiten" : "Neuer Lead"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {/* Essentials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" required className="md:col-span-2">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="lead-form-name" placeholder="z. B. Maria Müller" />
              </Field>
              <Field label="E-Mail">
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-form-email" />
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="lead-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Notizen" className="md:col-span-2">
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="lead-form-notes" placeholder="Woher kommt der Kontakt, nächster Schritt?" />
              </Field>
            </div>

            {/* Erweitert */}
            <details className="group border-t border-white/5 pt-3">
              <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1" data-testid="lead-form-advanced-toggle">
                <span className="group-open:rotate-90 transition inline-block">▸</span> Erweitert (Firma, Telefon, Quelle, Tags)
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <Field label="Firma">
                  <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lead-form-company" />
                </Field>
                <Field label="Telefon">
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-form-phone" />
                </Field>
                <Field label="Quelle" className="md:col-span-2">
                  <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="lead-form-source" placeholder="z. B. Instagram, Empfehlung, Landingpage" />
                </Field>
                <Field label="Tags (kommagetrennt)" className="md:col-span-2">
                  <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="lead-form-tags" />
                </Field>
              </div>
            </details>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="lead-form-cancel">Abbrechen</Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="lead-form-submit">
                {editing ? "Änderungen speichern" : "Lead anlegen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Lead löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              „{confirmDel?.name}" wird dauerhaft entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="lead-delete-confirm" className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-300 text-xs mb-1 block">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}
